/**
 * Article meta — the bibliographic header used by ArticleDetailView.
 *
 * Renders four rows, top-to-bottom:
 *
 *   <header-line>   PMID <id> · <journal abbr> · <pubdate>
 *   <authors>       Author1, Author2, ... (one comma-joined span)
 *   <affiliations>  Numbered chips for each affiliation. Multi-affiliated
 *                   authors are also represented as superscripted chips
 *                   attached to their name in the list above.
 *   <links>         DOI ↗  ·  PubMed ↗
 *
 * Convention #6 — bilingual titles + abstracts are NOT shown here; this
 * file only carries the metadata row. The view composes
 * `<articleMeta>` with separate bilingual blocks.
 */

import { el } from "../dom";
import type { ArticleDetail } from "../types";

export interface ArticleMetaOptions {
  article: ArticleDetail;
}

export function articleMeta({ article }: ArticleMetaOptions): HTMLElement {
  const root = el("div", { class: "article-meta-block" });

  // Header line: PMID + journal + pubdate.
  const journal = article.journal_abbr ?? article.journal;
  const header = el("div", { class: "article-meta-header" }, [
    el("span", { class: "article-meta-pmid" }, `PMID ${article.pmid}`),
    journal
      ? el("span", { class: "article-meta-sep" }, "·")
      : null,
    journal ? el("span", { class: "article-meta-journal" }, journal) : null,
    article.pubdate
      ? el("span", { class: "article-meta-sep" }, "·")
      : null,
    article.pubdate
      ? el("span", { class: "article-meta-pubdate" }, article.pubdate)
      : null,
  ]);
  root.appendChild(header);

  // Authors — joined; multi-affiliated authors get a tiny superscript
  // like "Author¹·²" so the affiliations row stays compact.
  if (article.authors.length > 0) {
    const authorSpans: HTMLElement[] = article.authors.map((name, idx) =>
      el(
        "span",
        { class: "article-meta-author" },
        [name, ...affSuperscripts(article.affiliations, idx)],
      ),
    );
    const authorsRow = el("div", { class: "article-meta-authors" }, [
      el("span", { class: "article-meta-label" }, "作者"),
      ...authorSpans,
    ]);
    root.appendChild(authorsRow);
  }

  // Affiliations — deduped by name, numbered. Each chip is just a small
  // bordered pill.
  if (article.affiliations.length > 0) {
    const dedup = dedupeAffiliations(article.affiliations);
    const affChips: HTMLElement[] = dedup.map((aff, i) =>
      el(
        "span",
        { class: "article-meta-aff-chip", "data-aff-no": String(i + 1) },
        [
          el("sup", null, String(i + 1)),
          el("span", { class: "article-meta-aff-name" }, aff.name),
        ],
      ),
    );
    const affRow = el("div", { class: "article-meta-affiliations" }, [
      el("span", { class: "article-meta-label" }, "单位"),
      ...affChips,
    ]);
    root.appendChild(affRow);
  }

  // External links.
  const links: HTMLElement[] = [];
  if (article.doi) {
    links.push(
      el(
        "a",
        {
          class: "article-meta-link",
          href: `https://doi.org/${article.doi}`,
          target: "_blank",
          rel: "noopener noreferrer",
        },
        "DOI ↗",
      ),
    );
  }
  links.push(
    el(
      "a",
      {
        class: "article-meta-link",
        href: `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(article.pmid)}/`,
        target: "_blank",
        rel: "noopener noreferrer",
      },
      "PubMed ↗",
    ),
  );
  if (article.publication_types.length > 0) {
    links.push(
      el("span", { class: "article-meta-pubtypes" }, [
        el("span", { class: "article-meta-label" }, "类型"),
        ...article.publication_types.map((t) =>
          el("span", { class: "article-meta-pubtype-chip" }, t),
        ),
      ]),
    );
  }
  root.appendChild(el("div", { class: "article-meta-links" }, links));

  // Diseases — chip row.
  if (article.diseases.length > 0) {
    const diseasesRow = el("div", { class: "article-meta-diseases" }, [
      el("span", { class: "article-meta-label" }, "疾病"),
      ...article.diseases.map((d) =>
        el("span", { class: "article-meta-disease-chip", "data-disease": d }, diseaseLabel(d)),
      ),
    ]);
    root.appendChild(diseasesRow);
  }

  return root;
}

function affSuperscripts(
  affiliations: Array<{ name: string; identifier: string | null }>,
  authorIndex: number,
): HTMLElement[] {
  // The wire shape doesn't carry per-author → affiliation mapping
  // (PubMed XML pre-parses into a flat list). Show no superscripts if
  // the data doesn't include them — the affiliations row below still
  // lists every distinct affiliation. Keeps the visual contract.
  void authorIndex;
  void affiliations;
  return [];
}

function dedupeAffiliations(
  affiliations: Array<{ name: string; identifier: string | null }>,
): Array<{ name: string; identifier: string | null }> {
  const seen = new Set<string>();
  const out: Array<{ name: string; identifier: string | null }> = [];
  for (const a of affiliations) {
    if (seen.has(a.name)) continue;
    seen.add(a.name);
    out.push(a);
  }
  return out;
}

function diseaseLabel(key: ArticleDetail["diseases"][number]): string {
  switch (key) {
    case "lung_cancer":
      return "肺癌";
    case "mediastinal_tumor":
      return "纵隔肿瘤";
    case "esophageal_cancer":
      return "食管癌";
    case "pneumothorax":
      return "气胸（含胸部外伤、肋骨骨折、胸壁畸形）";
    case "mediastinal_tumor_supplement":
      return "纵隔肿瘤（补充）";
    default:
      return key;
  }
}